import { useQuery } from "@tanstack/react-query";
import * as React from "react";
import {
  redactTraceValue,
  traceSnapshotSchema,
  traceSpanSchema,
  type TraceSnapshot,
  type TraceSpan,
} from "@zworks/console-contracts";
import { Badge } from "@/components/ui/badge";
import { EmptyState, ErrorState, LoadingState } from "@/components/states";
import { cn } from "@/lib/utils";
import { traceSnapshotQuery } from "@/lib/api";
import { TraceWaterfall } from "./tracing/trace-waterfall";
import { SpanInspector } from "./tracing/span-inspector";
import { formatDurationMs, formatMicroUSD, formatTokens } from "./tracing/trace-types";
import { AlertTriangle, Clock, Coins, Cpu, Layers, Search } from "lucide-react";
import { Input } from "@/components/ui/input";

export function TracePanel({
  projectId,
  runId,
  traceId,
  running,
}: {
  projectId: string;
  runId: string;
  traceId: string | null;
  running: boolean;
}) {
  const query = useQuery({
    ...traceSnapshotQuery(projectId, runId),
    enabled: Boolean(traceId),
    retry: false,
  });

  const [liveData, setLiveData] = React.useState<{ key: string; spans: TraceSpan[] }>({
    key: `${projectId}:${runId}`,
    spans: [],
  });
  const [selectedSpan, setSelectedSpan] = React.useState<TraceSpan | null>(null);
  const [searchQuery, setSearchQuery] = React.useState("");
  const cursor = React.useRef("");
  const currentKey = `${projectId}:${runId}`;

  React.useEffect(() => {
    cursor.current = "";
    if (!traceId || !running) return;
    let source: EventSource | undefined;
    let reconnectTimer: number | undefined;
    let stopped = false;

    const connect = () => {
      if (stopped) return;
      const suffix = cursor.current ? `?cursor=${encodeURIComponent(cursor.current)}` : "";
      source = new EventSource(
        `/api/v1/projects/${encodeURIComponent(projectId)}/runs/${encodeURIComponent(runId)}/trace/live${suffix}`,
      );
      source.addEventListener("span", (event) => {
        const message = event as MessageEvent<string>;
        let raw: Record<string, unknown>;
        try {
          raw = JSON.parse(message.data) as Record<string, unknown>;
        } catch {
          return;
        }
        const span = {
          spanId: raw.spanId,
          parentSpanId: typeof raw.parentSpanId === "string" ? raw.parentSpanId : null,
          name: raw.name,
          kind: raw.kind,
          status: raw.status,
          startedAt: raw.startTime,
          finishedAt: raw.endTime ?? null,
          durationMs: raw.durationMs,
          attributes: {
            ...(raw.attributes && typeof raw.attributes === "object"
              ? (raw.attributes as object)
              : {}),
            ...(raw.llm ? { llm: raw.llm } : {}),
            ...(raw.tool ? { tool: raw.tool } : {}),
            ...(raw.errorMessage ? { errorMessage: raw.errorMessage } : {}),
          },
        };
        const parsed = traceSpanSchema.safeParse(redactTraceValue(span));
        if (!parsed.success) return;
        if (message.lastEventId) cursor.current = message.lastEventId;
        setLiveData((current) => {
          const spans = current.key === currentKey ? current.spans : [];
          return spans.some((item) => item.spanId === parsed.data.spanId)
            ? current
            : { key: currentKey, spans: [...spans, parsed.data] };
        });
      });
      source.onerror = () => {
        source?.close();
        if (!stopped) reconnectTimer = window.setTimeout(connect, 1500);
      };
    };

    connect();
    return () => {
      stopped = true;
      source?.close();
      if (reconnectTimer) window.clearTimeout(reconnectTimer);
    };
  }, [projectId, runId, traceId, running, currentKey]);

  if (!traceId) {
    return (
      <EmptyState title="Trace not linked" description="This run has no Tracing Hub identifier." />
    );
  }

  if (query.isPending) {
    return <LoadingState label="Loading agent trace" rows={5} />;
  }

  if (query.isError) {
    return (
      <ErrorState
        title="Trace could not be loaded"
        description="The trace service is unavailable or has no recorded spans yet."
        requestId={query.error instanceof Error ? query.error.message : "unknown"}
        onRetry={() => void query.refetch()}
      />
    );
  }

  const snapshot: TraceSnapshot = traceSnapshotSchema.parse(redactTraceValue(query.data));
  const rawSpans = [
    ...snapshot.spans,
    ...(liveData.key === currentKey ? liveData.spans : []).filter(
      (live) => !snapshot.spans.some((saved) => saved.spanId === live.spanId),
    ),
  ];

  if (!rawSpans.length) {
    return (
      <EmptyState
        title="No spans yet"
        description={
          running
            ? "Waiting for the agent to emit trace events."
            : "No spans were recorded for this run."
        }
      />
    );
  }

  // Filter spans if search term provided
  const filteredSpans = searchQuery.trim()
    ? rawSpans.filter((s) => {
        const queryLower = searchQuery.toLowerCase();
        return (
          s.name.toLowerCase().includes(queryLower) ||
          s.kind.toLowerCase().includes(queryLower) ||
          s.spanId.toLowerCase().includes(queryLower)
        );
      })
    : rawSpans;

  const totalTokens = snapshot.totalTokens;
  const totalCostUSD = snapshot.costMicroUSD;
  const totalDuration = snapshot.durationMs;
  const errorCount = rawSpans.filter(
    (s) => s.status.toLowerCase() === "error" || Boolean(s.attributes.errorMessage),
  ).length;

  return (
    <div className="flex flex-col gap-4">
      {/* Metric summary bar */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div className="rounded-lg border border-border/60 bg-card p-3 shadow-2xs">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>Spans Recorded</span>
            <Layers className="size-3.5 text-muted-foreground" aria-hidden />
          </div>
          <div className="mt-1 flex items-baseline gap-2">
            <span className="font-mono text-xl font-bold">{rawSpans.length}</span>
            {running && (
              <span className="inline-flex items-center gap-1 text-xs font-medium text-chart">
                <span className="dot-indicator" /> Live
              </span>
            )}
          </div>
        </div>

        <div className="rounded-lg border border-border/60 bg-card p-3 shadow-2xs">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>Total Duration</span>
            <Clock className="size-3.5 text-muted-foreground" aria-hidden />
          </div>
          <div className="mt-1 font-mono text-xl font-bold">{formatDurationMs(totalDuration)}</div>
        </div>

        <div className="rounded-lg border border-border/60 bg-card p-3 shadow-2xs">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>Token Volume</span>
            <Cpu className="size-3.5 text-muted-foreground" aria-hidden />
          </div>
          <div className="mt-1 font-mono text-xl font-bold">{formatTokens(totalTokens)}</div>
        </div>

        <div className="rounded-lg border border-border/60 bg-card p-3 shadow-2xs">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>Estimated Cost</span>
            <Coins className="size-3.5 text-chart" aria-hidden />
          </div>
          <div className="mt-1 font-mono text-xl font-bold text-chart">
            {formatMicroUSD(totalCostUSD)}
          </div>
        </div>
      </div>

      {/* Main interactive area with Search & Waterfall/Inspector Split */}
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between gap-2">
          <div className="relative max-w-sm flex-1">
            <Search
              className="absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground"
              aria-hidden
            />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search spans, tools, models..."
              className="h-8 pl-8 text-xs"
            />
          </div>
          <div className="flex items-center gap-2">
            {errorCount > 0 && (
              <Badge variant="danger" className="gap-1 font-mono text-xs">
                <AlertTriangle className="size-3" aria-hidden /> {errorCount} failed
              </Badge>
            )}
            <Badge variant="outline" className="font-mono text-xs">
              Trace ID: {traceId}
            </Badge>
          </div>
        </div>

        <div
          className={cn(
            "grid gap-4 transition-all duration-200",
            selectedSpan ? "lg:grid-cols-[minmax(0,1.3fr)_minmax(340px,0.9fr)]" : "grid-cols-1",
          )}
        >
          {/* Left Panel: Hierarchical Gantt Waterfall */}
          <div className="min-h-[420px] max-h-[640px] flex flex-col">
            <TraceWaterfall
              spans={filteredSpans}
              selectedSpanId={selectedSpan?.spanId ?? null}
              onSelectSpan={(span) => setSelectedSpan(span)}
              totalDurationMs={totalDuration}
            />
          </div>

          {/* Right Panel: Span Inspector (shown when span selected) */}
          {selectedSpan && (
            <div className="min-h-[420px] max-h-[640px] overflow-hidden rounded-lg shadow-2xs">
              <SpanInspector span={selectedSpan} onClose={() => setSelectedSpan(null)} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
